/**
 * Language to BCP 47 locale (for Intl). Supports app codes including "se" for Swedish.
 */
const LANGUAGE_TO_LOCALE = {
  de: "de-DE",
  fr: "fr-FR",
  en: "en-US",
  es: "es-ES",
  se: "sv-SE",
  sv: "sv-SE",
};

const DEFAULT_LOCALE = "fr-FR";

/**
 * Returns the locale string for a given app language code.
 * @param {string} language - e.g. "fr", "en", "es", "se"
 * @returns {string}
 */
const getLocaleForLanguage = (language) => {
  if (!language) return DEFAULT_LOCALE;
  return LANGUAGE_TO_LOCALE[language] ?? DEFAULT_LOCALE;
};

/**
 * Returns the 12 month names for a given language (full form, e.g. "janvier", "January").
 * Uses Intl so no static list to maintain; add a language by adding LANGUAGE_TO_LOCALE.
 * @param {string} language - e.g. "fr", "en", "es", "se"
 * @returns {string[]} Array of 12 month names (index 0 = January)
 */
const getMonthNames = (language) => {
  const locale = getLocaleForLanguage(language);
  const formatter = new Intl.DateTimeFormat(locale, { month: "long" });
  return Array.from({ length: 12 }, (_, i) =>
    formatter.format(new Date(2000, i, 1)),
  );
};

/**
 * Returns the month name for a given month index (0–11) and language.
 * @param {string} language - e.g. "fr", "en", "es", "se"
 * @param {number} monthIndex - 0–11
 * @returns {string}
 */
const getMonthName = (language, monthIndex) => {
  const names = getMonthNames(language);
  const i = Math.max(0, Math.min(11, Math.floor(monthIndex)));
  return names[i];
};

/**
 * Format date as "Vendredi 02 janvier à 17h40" (language-aware separator).
 */
export const formatCommentDate = (date, language = "fr") => {
  if (!date) return "";
  const d = new Date(date);
  if (Number.isNaN(d.getTime())) return "";
  const locale = getLocaleForLanguage(language);
  const dayName = d.toLocaleDateString(locale, { weekday: "long" });
  const capitalizedDayName = dayName.charAt(0).toUpperCase() + dayName.slice(1);
  const day = String(d.getDate()).padStart(2, "0");
  const month = getMonthName(language, d.getMonth());
  const hours = String(d.getHours()).padStart(2, "0");
  const minutes = String(d.getMinutes()).padStart(2, "0");
  const lang = (language || "fr").split("-")[0];
  const separator = lang === "fr" ? "à" : lang === "es" ? "a las" : "at";
  return `${capitalizedDayName} ${day} ${month} ${separator} ${hours}h${minutes}`;
};
